import React from 'react';
import Dialog from '@react/react-spectrum/Dialog';
import Select from '@react/react-spectrum/Select';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import GroupAddIcon from '@material-ui/icons/GroupAdd';
import RestoreIcon from '@material-ui/icons/Restore';
import PersonIcon from '@material-ui/icons/Person';
import PropensityLevel from '../../constants/PropensityLevel';
import './PropensityLevelDialog.css';

export interface PropensitySaveResult {
  success: boolean;
  // Human-readable failure reason (4xx validation error, 5xx server error, or a network/CORS
  // message) — shown inline in this dialog. Every failure stays local to this dialog; nothing
  // here escalates to the app-wide ErrorDialog.
  message?: string;
}

interface PropensityLevelDialogProps {
  isOpen: boolean;
  customerId: string;
  churnLevel: PropensityLevel;
  seatAdditionLevel: PropensityLevel;
  handleSave: (
    churnLevel: PropensityLevel | undefined,
    seatAdditionLevel: PropensityLevel | undefined
  ) => Promise<PropensitySaveResult>;
  handleDismiss: () => void;
  handleResetToDefault: () => Promise<PropensitySaveResult>;
}

type SaveStatus = 'idle' | 'success' | 'failed';

const levelOptions = [
  {label: 'High', value: PropensityLevel.HIGH},
  {label: 'Medium', value: PropensityLevel.MEDIUM},
  {label: 'Low', value: PropensityLevel.LOW},
  {label: 'Not set (opt out)', value: PropensityLevel.NONE}
];

function PropensityLevelDialog(props: PropensityLevelDialogProps) {
  const {
    isOpen,
    customerId,
    churnLevel,
    seatAdditionLevel,
    handleSave,
    handleDismiss,
    handleResetToDefault
  } = props;

  const [selectedChurnLevel, setSelectedChurnLevel] = React.useState(churnLevel);
  const [selectedSeatAdditionLevel, setSelectedSeatAdditionLevel] = React.useState(seatAdditionLevel);
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveStatus, setSaveStatus] = React.useState<SaveStatus>('idle');
  const [failureMessage, setFailureMessage] = React.useState('');

  // Keep the selections in sync with the parent's baseline — both on open and whenever the
  // baseline itself moves (e.g. right after a successful save). Deliberately does not touch
  // isSaving/saveStatus so the "Done" confirmation set by onConfirm below isn't immediately
  // wiped out by this same effect firing on the baseline change that save just caused.
  React.useEffect(() => {
    setSelectedChurnLevel(churnLevel);
    setSelectedSeatAdditionLevel(seatAdditionLevel);
  }, [churnLevel, seatAdditionLevel]);

  // Reset transient saving/success/failure state only when the dialog is freshly (re)opened.
  React.useEffect(() => {
    if (isOpen) {
      setIsSaving(false);
      setSaveStatus('idle');
      setFailureMessage('');
    }
  }, [isOpen]);

  const churnChanged = selectedChurnLevel !== churnLevel;
  const seatAdditionChanged = selectedSeatAdditionLevel !== seatAdditionLevel;
  const hasPendingChanges = churnChanged || seatAdditionChanged;

  const onSelectChurnLevel = (value: PropensityLevel) => {
    setSelectedChurnLevel(value);
    setSaveStatus('idle');
  };

  const onSelectSeatAdditionLevel = (value: PropensityLevel) => {
    setSelectedSeatAdditionLevel(value);
    setSaveStatus('idle');
  };

  const onConfirm = async () => {
    setIsSaving(true);
    setSaveStatus('idle');
    const result = await handleSave(
      churnChanged ? selectedChurnLevel : undefined,
      seatAdditionChanged ? selectedSeatAdditionLevel : undefined
    );
    setIsSaving(false);
    setSaveStatus(result.success ? 'success' : 'failed');
    setFailureMessage(result.success ? '' : (result.message || ''));
    // Keep the dialog open either way so the confirmation/failure indicator is visible; this
    // return value is otherwise unused since this app closes dialogs by setting isOpen=false
    // from the parent, not via Dialog's own onClose.
    return false;
  };

  const onResetClick = async () => {
    setIsSaving(true);
    setSaveStatus('idle');
    const result = await handleResetToDefault();
    setIsSaving(false);
    setSaveStatus(result.success ? 'success' : 'failed');
    setFailureMessage(result.success ? '' : (result.message || ''));
  };

  return (
    <Dialog
      title={
        <div className='propensity-dialog-title'>
          <span>Set propensity level</span>
          <IconButton size='small' aria-label='Close' onClick={handleDismiss} className='propensity-dialog-close'>
            <CloseIcon fontSize='small' />
          </IconButton>
        </div>
      }
      open={isOpen}
      confirmLabel='Save'
      cancelLabel='Cancel'
      confirmDisabled={!hasPendingChanges || isSaving}
      onConfirm={onConfirm}
      onCancel={() => handleDismiss()}
    >
      <div className='propensity-dialog-content'>
        <div className='propensity-customer-bar'>
          <PersonIcon fontSize='small' className='propensity-customer-bar-icon' />
          <span className='propensity-customer-label'>Customer</span>
          <span className='propensity-customer-value'>{customerId}</span>
        </div>

        <div className='propensity-field-row'>
          <div className='propensity-field-label'>
            <TrendingDownIcon fontSize='small' className='propensity-field-label-icon' />
            <span className='propensity-field-label-text'>Churn level</span>
          </div>
          <Select
            aria-label='Churn Level'
            name='churnLevel'
            value={selectedChurnLevel}
            options={levelOptions}
            disabled={isSaving}
            onSelect={onSelectChurnLevel}
          />
        </div>

        <div className='propensity-field-row propensity-field-row--last'>
          <div className='propensity-field-label'>
            <GroupAddIcon fontSize='small' className='propensity-field-label-icon' />
            <span className='propensity-field-label-text'>Seat addition</span>
          </div>
          <Select
            aria-label='Seat Addition Level'
            name='seatAdditionLevel'
            value={selectedSeatAdditionLevel}
            options={levelOptions}
            disabled={isSaving}
            onSelect={onSelectSeatAdditionLevel}
          />
        </div>

        <div className='propensity-footer'>
          <div
            role='button'
            tabIndex={0}
            onClick={isSaving ? undefined : onResetClick}
            onKeyDown={e => {
              if (!isSaving && (e.key === 'Enter' || e.key === ' ')) onResetClick();
            }}
            className={`propensity-reset${isSaving ? ' propensity-reset--disabled' : ''}`}
          >
            <RestoreIcon fontSize='small' />
            Reset to default
          </div>

          <div className='propensity-status-row'>
            {saveStatus === 'success' && !hasPendingChanges && (
              <div className='propensity-status-chip propensity-status-chip--success'>
                <CheckCircleIcon className='propensity-status-chip-icon' /> Done
              </div>
            )}
            {saveStatus === 'failed' && (
              <div className='propensity-status-chip propensity-status-chip--failed'>
                <CancelIcon className='propensity-status-chip-icon' /> Failed{failureMessage ? `: ${failureMessage}` : ''}
              </div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}

export default PropensityLevelDialog;
